import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBrands = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data);
    } catch {
      toast.error('Failed to load brands');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  return (
    <div className="min-h-screen pt-20 bg-[#0a0a0a]" data-testid="brands-page">
      {/* Header */}
      <div className="py-12 md:py-16 px-6 md:px-12 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C41E3A] mb-4">Fashion Icons</p>
          <h1 className="text-4xl md:text-5xl font-serif italic text-white">Luxury Brands</h1>
          <p className="text-gray-400 mt-4 max-w-2xl">
            Explore the world's most prestigious fashion houses. From Parisian haute couture to Italian craftsmanship, 
            discover the brands that define luxury fashion.
          </p>
        </div>
      </div>

      {/* Brands Grid */}
      <div className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((num) => (
                <div key={`skeleton-${num}`} className="h-[500px] skeleton rounded-sm" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {brands.map((brand) => (
                <Link
                  key={brand.id}
                  to={`/brands/${brand.slug}`}
                  className="relative h-[500px] overflow-hidden grayscale hover:grayscale-0 transition-all duration-700 ease-out group"
                  data-testid={`brand-card-${brand.slug}`}
                >
                  {/* Image */}
                  <div className="absolute inset-0">
                    <img
                      src={brand.cover_image}
                      alt={brand.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-8">
                    <span className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-2">
                      {brand.country} · Est. {brand.founded}
                    </span>
                    <h3 className="text-3xl font-serif italic text-white mb-4">{brand.name}</h3>
                    
                    {/* Hover reveal */}
                    <div className="flex items-center gap-2 text-white/0 group-hover:text-white transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                      <span className="text-sm uppercase tracking-wider">Explore Collection</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandsPage;
